import type {
  ApiKeySummary,
  OrgInvitationSummary,
  OrgMemberSummary,
  OrgUnitSummary,
} from '@nadoutong/contracts';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useKeysList, useRevokeApiKey } from '../../api/useKeys.ts';
import {
  useCreateOrgInvitation,
  useOrgInvitations,
  useOrgMembers,
  useOrgUnits,
  useRevokeOrgInvitation,
  useUpdateOrgMember,
} from '../../api/useOrgs.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { useToast } from '../../components/Toast.tsx';
import { formatTimestamp } from '../../components/formatters.ts';
import { useClipboard } from '../../hooks/useClipboard.tsx';
import { hasPermission, useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  Drawer,
  EmptyState,
  FormField,
  Input,
  KpiCard,
  PageHeader,
  Panel,
  Select,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { formatTeamApiError, roleLabelKey } from './shared.ts';

/**
 * `/team/members` — member directory + pending invitations (epic #897
 * PR 7). This page is the ONE home for invitations (an invitation is a
 * pending member); the former governance tab dissolved into the summary
 * cards' hint lines.
 *
 * Interaction contract:
 *   - Inline org-unit assignment on a row is a non-destructive
 *     `ui/Select` that mutates on change and toasts on success; its
 *     failure reports inline under the select (single-channel errors).
 *   - Invitation revoke gains a danger `ConfirmDialog` (the old page
 *     mutated on one click); member API-key revoke keeps its confirm.
 *   - The invite flow (email + role → invite link with copy) is a
 *     LOCAL rebuild of the shared Settings-module InviteMemberModal /
 *     InviteLinkModal pair on `ui/Drawer` — no shared-package imports
 *     here.
 *   - Panels load and fail independently: a members failure never
 *     blanks the invitations panel, and vice versa.
 */

type TranslateFn = ReturnType<typeof useT>;
type UnitFilter = 'all' | 'unassigned' | string;

const UNASSIGNED = 'unassigned';
const ALL = 'all';

export function TeamMembers(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const orgId = user?.orgId ?? undefined;
  const canReadMembers = hasPermission(user, 'org:members:read');
  const canManageMembers = hasPermission(user, 'org:members:manage');
  const canManageInvitations = hasPermission(user, 'org:invitations:manage');
  const canReadOrgKeys = hasPermission(user, 'org:api_keys:read');
  const canManageOrgKeys = hasPermission(user, 'org:api_keys:manage');

  const membersQ = useOrgMembers(canReadMembers ? orgId : undefined);
  const unitsQ = useOrgUnits(canReadMembers ? orgId : undefined);
  const invitationsQ = useOrgInvitations(canReadMembers ? orgId : undefined);
  const keysQ = useKeysList(canReadMembers && canReadOrgKeys);
  const updateMember = useUpdateOrgMember();
  const revokeInvitation = useRevokeOrgInvitation();

  const [search, setSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState<UnitFilter>(ALL);
  const [assignError, setAssignError] = useState<{
    readonly accountId: string;
    readonly message: string;
  } | null>(null);
  const [keysMember, setKeysMember] = useState<OrgMemberSummary | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [revokeInviteTarget, setRevokeInviteTarget] = useState<OrgInvitationSummary | null>(null);
  const [revokeInviteError, setRevokeInviteError] = useState<string | null>(null);

  const members = useMemo(() => membersQ.data?.members ?? [], [membersQ.data]);
  const units = unitsQ.data?.units ?? [];
  const invitations = invitationsQ.data?.invitations ?? [];
  const activeOrgKeys = useMemo(
    () => keysQ.data?.keys.filter((key) => key.revokedAt === null) ?? [],
    [keysQ.data],
  );
  const memberKeyCounts = useMemo(
    () => countMemberApiKeys(activeOrgKeys, members),
    [activeOrgKeys, members],
  );
  const filteredMembers = useMemo(
    () => filterMembers(members, search, unitFilter),
    [members, search, unitFilter],
  );

  const managerCount = members.filter(
    (member) => member.role === 'owner' || member.role === 'admin',
  ).length;
  const assignedCount = members.filter((member) => member.orgUnitId !== null).length;
  const limitedCount = members.filter((member) => member.monthlyLimitUsdCents !== null).length;

  if (!canReadMembers) {
    return (
      <section data-testid="team-members-page" className="flex flex-col gap-4">
        <PageHeader title={t('team.members.title')} testId="team-members-header" />
        <Panel caption={t('team.members.directory.title')} padded>
          <p className="m-0 text-dense text-paper-low" data-testid="team-forbidden">
            {t('team.forbidden')}
          </p>
        </Panel>
      </section>
    );
  }

  function handleAssign(member: OrgMemberSummary, next: string): void {
    if (orgId === undefined) return;
    setAssignError(null);
    updateMember.mutate(
      {
        orgId,
        accountId: member.accountId,
        body: { primaryOrgUnitId: next === UNASSIGNED ? null : next },
      },
      {
        onSuccess: () => {
          toast.success(t('team.members.toast.unit_updated'));
        },
        onError: (err) => {
          setAssignError({
            accountId: member.accountId,
            message: formatTeamApiError(err, t('team.members.error.unit_update')),
          });
        },
      },
    );
  }

  async function handleRevokeInvitation(): Promise<void> {
    if (orgId === undefined || revokeInviteTarget === null) return;
    setRevokeInviteError(null);
    try {
      await revokeInvitation.mutateAsync({ orgId, invitationId: revokeInviteTarget.id });
      setRevokeInviteTarget(null);
      toast.success(t('team.invitations.toast.revoked'));
    } catch (err) {
      setRevokeInviteError(formatTeamApiError(err, t('team.invitations.revoke_error')));
    }
  }

  return (
    <section data-testid="team-members-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('team.members.title')}
        sub={t('team.members.subtitle')}
        testId="team-members-header"
        actions={
          canManageInvitations ? (
            <Button
              variant="primary"
              onClick={() => setInviteOpen(true)}
              data-testid="team-invite-button"
            >
              {t('team.members.invite')}
            </Button>
          ) : undefined
        }
      />

      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
        data-testid="team-members-summary"
      >
        <KpiCard
          testId="team-summary-members"
          label={t('team.members.summary.members')}
          value={membersQ.data === undefined ? '--' : String(members.length)}
          delta={{
            text: t('team.members.summary.members_hint', { count: managerCount }),
            tone: 'neutral',
          }}
        />
        <KpiCard
          testId="team-summary-units"
          label={t('team.members.summary.units')}
          value={unitsQ.data === undefined ? '--' : String(unitsQ.data.summary.totalUnits)}
          delta={{
            text: t('team.members.summary.units_hint', { count: assignedCount }),
            tone: 'neutral',
          }}
        />
        <KpiCard
          testId="team-summary-unassigned"
          label={t('team.members.summary.unassigned')}
          value={unitsQ.data === undefined ? '--' : String(unitsQ.data.summary.unassignedMembers)}
          delta={{
            text: t('team.members.summary.unassigned_hint', { count: limitedCount }),
            tone: 'neutral',
          }}
        />
        <KpiCard
          testId="team-summary-pending"
          label={t('team.members.summary.pending')}
          value={invitationsQ.data === undefined ? '--' : String(invitations.length)}
        />
      </div>

      <Panel
        caption={t('team.members.directory.title')}
        count={membersQ.data === undefined ? undefined : filteredMembers.length}
        testId="team-members-directory"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              placeholder={t('team.members.filter.search_placeholder')}
              aria-label={t('team.members.filter.search_label')}
              className="w-56"
              data-testid="team-members-search"
            />
            <Select
              value={unitFilter}
              onValueChange={(next) => setUnitFilter(next)}
              options={[
                { value: ALL, label: t('team.members.filter.all_units') },
                { value: UNASSIGNED, label: t('team.members.filter.unassigned') },
                ...units.map((unit) => ({ value: unit.id, label: unit.path })),
              ]}
              ariaLabel={t('team.members.filter.unit_label')}
              className="w-48"
              testId="team-members-unit-filter"
            />
          </div>
        }
      >
        {membersQ.isLoading ? (
          <TableSkeleton rows={4} testId="team-members-loading" />
        ) : membersQ.isError ? (
          <div className="p-4">
            <PanelError
              message={formatTeamApiError(membersQ.error, t('team.members.error.load'))}
              retryLabel={t('team.retry')}
              onRetry={() => {
                void membersQ.refetch();
              }}
              testId="team-members-error"
            />
          </div>
        ) : members.length === 0 ? (
          <EmptyState testId="team-members-empty" title={t('team.members.empty')} />
        ) : (
          <>
            {unitsQ.isError ? (
              <div className="border-b border-line-soft p-4">
                <PanelError
                  message={formatTeamApiError(unitsQ.error, t('team.members.error.units_load'))}
                  retryLabel={t('team.retry')}
                  onRetry={() => {
                    void unitsQ.refetch();
                  }}
                  testId="team-members-units-error"
                />
              </div>
            ) : null}
            {canReadOrgKeys && keysQ.isError ? (
              <div className="border-b border-line-soft p-4">
                <PanelError
                  message={formatTeamApiError(keysQ.error, t('team.members.keys.error'))}
                  retryLabel={t('team.retry')}
                  onRetry={() => {
                    void keysQ.refetch();
                  }}
                  testId="team-members-keys-error"
                />
              </div>
            ) : null}
            {filteredMembers.length === 0 ? (
              <EmptyState testId="team-members-no-match" title={t('team.members.no_match')} />
            ) : (
              <MemberDirectoryTable
                t={t}
                members={filteredMembers}
                units={units}
                canManageMembers={canManageMembers}
                canReadOrgKeys={canReadOrgKeys}
                keyCounts={memberKeyCounts}
                keysLoading={keysQ.isLoading}
                assignPending={updateMember.isPending}
                assignError={assignError}
                onAssign={handleAssign}
                onManageKeys={setKeysMember}
              />
            )}
          </>
        )}
      </Panel>

      <Panel
        caption={t('team.invitations.title')}
        count={invitationsQ.data === undefined ? undefined : invitations.length}
        testId="team-invitations-panel"
      >
        {invitationsQ.isLoading ? (
          <TableSkeleton rows={2} testId="team-invitations-loading" />
        ) : invitationsQ.isError ? (
          <div className="p-4">
            <PanelError
              message={formatTeamApiError(invitationsQ.error, t('team.invitations.error.load'))}
              retryLabel={t('team.retry')}
              onRetry={() => {
                void invitationsQ.refetch();
              }}
              testId="team-invitations-error"
            />
          </div>
        ) : invitations.length === 0 ? (
          <EmptyState testId="team-invitations-empty" title={t('team.invitations.empty')} />
        ) : (
          <Table className="rounded-none border-0" testId="team-invitations-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('team.invitations.column.email')}</Th>
                <Th>{t('team.invitations.column.role')}</Th>
                <Th>{t('team.invitations.column.expires')}</Th>
                {canManageInvitations ? <Th>{t('team.invitations.column.actions')}</Th> : null}
              </Tr>
            </THead>
            <TBody>
              {invitations.map((invitation) => (
                <Tr key={invitation.id} data-testid={`team-invitations-row-${invitation.id}`}>
                  <Td mono emphasis heading>
                    {invitation.email}
                  </Td>
                  <Td label={t('team.invitations.column.role')}>
                    {t(roleLabelKey(invitation.role))}
                  </Td>
                  <Td mono label={t('team.invitations.column.expires')}>
                    {formatTimestamp(invitation.expiresAt)}
                  </Td>
                  {canManageInvitations ? (
                    <Td label={t('team.invitations.column.actions')} stack>
                      <div className="flex items-center justify-end max-md:justify-start">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setRevokeInviteError(null);
                            setRevokeInviteTarget(invitation);
                          }}
                          data-testid={`team-invitations-revoke-${invitation.id}`}
                        >
                          {t('team.invitations.revoke')}
                        </Button>
                      </div>
                    </Td>
                  ) : null}
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      {keysMember !== null ? (
        <MemberKeysDialog
          t={t}
          member={keysMember}
          keys={keysForMember(activeOrgKeys, keysMember)}
          canRevoke={canManageOrgKeys}
          onClose={() => setKeysMember(null)}
        />
      ) : null}

      {inviteOpen && orgId !== undefined ? (
        <InviteDrawer t={t} orgId={orgId} onClose={() => setInviteOpen(false)} />
      ) : null}

      {revokeInviteTarget !== null ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !revokeInvitation.isPending) {
              setRevokeInviteTarget(null);
              setRevokeInviteError(null);
            }
          }}
          tone="danger"
          title={t('team.invitations.revoke_title')}
          body={
            <div className="flex flex-col gap-2">
              <p className="m-0">
                {t('team.invitations.revoke_body', { email: revokeInviteTarget.email })}
              </p>
              {revokeInviteError !== null ? (
                <p
                  role="alert"
                  data-testid="team-invitations-revoke-error"
                  className="m-0 text-label text-bad"
                >
                  {revokeInviteError}
                </p>
              ) : null}
            </div>
          }
          confirmLabel={t('team.invitations.revoke_confirm')}
          cancelLabel={t('team.invitations.revoke_cancel')}
          closeLabel={t('team.invitations.close')}
          loading={revokeInvitation.isPending}
          testId="team-invitations-revoke-dialog"
          onConfirm={() => {
            void handleRevokeInvitation();
          }}
        />
      ) : null}
    </section>
  );
}

interface MemberDirectoryTableProps {
  readonly t: TranslateFn;
  readonly members: readonly OrgMemberSummary[];
  readonly units: readonly OrgUnitSummary[];
  readonly canManageMembers: boolean;
  readonly canReadOrgKeys: boolean;
  readonly keyCounts: ReadonlyMap<string, number>;
  readonly keysLoading: boolean;
  readonly assignPending: boolean;
  readonly assignError: { readonly accountId: string; readonly message: string } | null;
  readonly onAssign: (member: OrgMemberSummary, next: string) => void;
  readonly onManageKeys: (member: OrgMemberSummary) => void;
}

function MemberDirectoryTable({
  t,
  members,
  units,
  canManageMembers,
  canReadOrgKeys,
  keyCounts,
  keysLoading,
  assignPending,
  assignError,
  onAssign,
  onManageKeys,
}: MemberDirectoryTableProps): JSX.Element {
  return (
    <Table className="rounded-none border-0" testId="team-members-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('team.members.column.member')}</Th>
          <Th>{t('team.members.column.unit')}</Th>
          <Th>{t('team.members.column.role')}</Th>
          <Th>{t('team.members.column.permissions')}</Th>
          <Th>{t('team.members.column.keys')}</Th>
          <Th>{t('team.members.column.joined')}</Th>
        </Tr>
      </THead>
      <TBody>
        {members.map((member) => {
          const name = member.displayName ?? member.email;
          return (
            <Tr key={member.accountId} data-testid={`team-members-row-${member.accountId}`}>
              <Td heading>
                <span className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-signal-dim font-mono text-micro text-signal"
                  >
                    {avatarInitial(member)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-paper">{name}</span>
                    <span className="block truncate font-mono text-micro text-paper-low">
                      {member.email}
                    </span>
                  </span>
                </span>
              </Td>
              <Td label={t('team.members.column.unit')} stack>
                {canManageMembers ? (
                  <div className="flex min-w-0 flex-col gap-1">
                    <Select
                      value={member.orgUnitId ?? UNASSIGNED}
                      onValueChange={(next) => onAssign(member, next)}
                      options={[
                        { value: UNASSIGNED, label: t('team.members.filter.unassigned') },
                        ...units.map((unit) => ({ value: unit.id, label: unit.path })),
                      ]}
                      disabled={assignPending}
                      ariaLabel={t('team.members.unit.aria', { member: name })}
                      className="md:w-44"
                      testId={`team-members-unit-${member.accountId}`}
                    />
                    {assignError !== null && assignError.accountId === member.accountId ? (
                      <p
                        role="alert"
                        data-testid={`team-members-unit-error-${member.accountId}`}
                        className="m-0 text-label text-bad"
                      >
                        {assignError.message}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <span className="font-mono text-micro text-paper-mid">
                    {member.orgUnitPath ?? member.department ?? t('team.members.filter.unassigned')}
                  </span>
                )}
              </Td>
              <Td label={t('team.members.column.role')}>{t(roleLabelKey(member.role))}</Td>
              <Td label={t('team.members.column.permissions')}>
                {member.modelPermissionMode === 'custom' ? (
                  <Chip
                    tone="signal"
                    lamp={false}
                    testId={`team-members-permission-${member.accountId}`}
                  >
                    {t('team.members.permission.limited')}
                  </Chip>
                ) : (
                  <Chip
                    tone="neutral"
                    lamp={false}
                    testId={`team-members-permission-${member.accountId}`}
                  >
                    {t('team.members.permission.unrestricted')}
                  </Chip>
                )}
              </Td>
              <Td label={t('team.members.column.keys')}>
                {!canReadOrgKeys ? (
                  <span className="font-mono text-micro text-paper-low">--</span>
                ) : member.role === 'owner' ? (
                  <span className="text-label text-paper-low">
                    {t('team.members.keys.owner_note')}
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={keysLoading}
                    onClick={() => onManageKeys(member)}
                    aria-label={t('team.members.keys.manage', { member: name })}
                    data-testid={`team-members-keys-${member.accountId}`}
                  >
                    <span className="font-mono">
                      {keysLoading
                        ? t('team.members.keys.loading')
                        : String(keyCounts.get(member.accountId) ?? 0)}
                    </span>
                  </Button>
                )}
              </Td>
              <Td mono label={t('team.members.column.joined')}>
                {formatTimestamp(member.joinedAt)}
              </Td>
            </Tr>
          );
        })}
      </TBody>
    </Table>
  );
}

interface MemberKeysDialogProps {
  readonly t: TranslateFn;
  readonly member: OrgMemberSummary;
  readonly keys: readonly ApiKeySummary[];
  readonly canRevoke: boolean;
  readonly onClose: () => void;
}

/**
 * Local rebuild of the old MemberApiKeysModal on `ui/Dialog`: lists the
 * member's active keys; revoke runs through a danger `ConfirmDialog`
 * (the confirm this flow always had). Exactly one modal is mounted at a
 * time — while the confirm is up it replaces the list dialog, and
 * cancelling returns to it.
 */
function MemberKeysDialog({
  t,
  member,
  keys,
  canRevoke,
  onClose,
}: MemberKeysDialogProps): JSX.Element {
  const toast = useToast();
  const revokeM = useRevokeApiKey();
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const memberName = member.displayName ?? member.email;

  async function handleRevoke(): Promise<void> {
    if (revokeTarget === null) return;
    setRevokeError(null);
    try {
      await revokeM.mutateAsync(revokeTarget.id);
      setRevokeTarget(null);
      toast.success(t('team.members.keys.toast.revoked'));
    } catch (err) {
      setRevokeError(formatTeamApiError(err, t('team.members.keys.revoke_error')));
    }
  }

  if (revokeTarget !== null) {
    return (
      <ConfirmDialog
        open
        onOpenChange={(open) => {
          if (!open && !revokeM.isPending) {
            setRevokeTarget(null);
            setRevokeError(null);
          }
        }}
        tone="danger"
        title={t('team.members.keys.revoke_title')}
        body={
          <div className="flex flex-col gap-2">
            <p className="m-0">{t('team.members.keys.revoke_body')}</p>
            <p className="m-0 font-mono text-label text-paper-mid">
              {apiKeyLabel(revokeTarget)} · {revokeTarget.prefix}
            </p>
            {revokeError !== null ? (
              <p
                role="alert"
                data-testid="team-member-key-revoke-error"
                className="m-0 text-label text-bad"
              >
                {revokeError}
              </p>
            ) : null}
          </div>
        }
        confirmLabel={t('team.members.keys.revoke_confirm')}
        cancelLabel={t('team.members.keys.revoke_cancel')}
        closeLabel={t('team.members.keys.close')}
        loading={revokeM.isPending}
        testId="team-member-key-revoke-dialog"
        onConfirm={() => {
          void handleRevoke();
        }}
      />
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !revokeM.isPending) onClose();
      }}
      title={t('team.members.keys.dialog_title', { member: memberName })}
      description={t('team.members.keys.dialog_subtitle')}
      size="lg"
      closeLabel={t('team.members.keys.close')}
      testId="team-member-keys-dialog"
    >
      {keys.length === 0 ? (
        <EmptyState testId="team-member-keys-empty" title={t('team.members.keys.empty')} />
      ) : (
        <Table testId="team-member-keys-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('team.members.keys.column.name')}</Th>
              <Th>{t('team.members.keys.column.prefix')}</Th>
              <Th>{t('team.members.keys.column.created')}</Th>
              <Th>{t('team.members.keys.column.last_used')}</Th>
              {canRevoke ? <Th>{t('team.members.keys.column.actions')}</Th> : null}
            </Tr>
          </THead>
          <TBody>
            {keys.map((key) => (
              <Tr key={key.id} data-testid={`team-member-key-row-${key.id}`}>
                <Td emphasis heading>
                  {apiKeyLabel(key)}
                </Td>
                <Td mono label={t('team.members.keys.column.prefix')}>
                  {key.prefix}
                </Td>
                <Td mono label={t('team.members.keys.column.created')}>
                  {formatTimestamp(key.createdAt)}
                </Td>
                <Td mono label={t('team.members.keys.column.last_used')}>
                  {key.lastUsedAt === null
                    ? t('team.members.keys.never_used')
                    : formatTimestamp(key.lastUsedAt)}
                </Td>
                {canRevoke ? (
                  <Td label={t('team.members.keys.column.actions')} stack>
                    <div className="flex items-center justify-end max-md:justify-start">
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={revokeM.isPending}
                        onClick={() => {
                          setRevokeError(null);
                          setRevokeTarget(key);
                        }}
                        data-testid={`team-member-key-revoke-${key.id}`}
                      >
                        {t('team.members.keys.revoke')}
                      </Button>
                    </div>
                  </Td>
                ) : null}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Dialog>
  );
}

interface InviteDrawerProps {
  readonly t: TranslateFn;
  readonly orgId: string;
  readonly onClose: () => void;
}

/**
 * Invite flow: email + role form, then an in-drawer success view with
 * the one-time invite link and a copy button (useClipboard). Validation
 * and submit failures render inline; only the created confirmation
 * toasts.
 */
function InviteDrawer({ t, orgId, onClose }: InviteDrawerProps): JSX.Element {
  const toast = useToast();
  const createM = useCreateOrgInvitation();
  const { copy, copied } = useClipboard();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setEmailError(null);
    setFormError(null);
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setEmailError(t('team.invite.email_required'));
      return;
    }
    try {
      const result = await createM.mutateAsync({ orgId, body: { email: trimmed, role } });
      toast.success(t('team.invite.toast.created'));
      setInviteUrl(result.inviteUrl);
    } catch (err) {
      setFormError(formatTeamApiError(err, t('team.invite.email_required')));
    }
  }

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open && !createM.isPending) onClose();
      }}
      title={t('team.invite.title')}
      description={t('team.invite.description')}
      closeLabel={t('team.invite.close')}
      testId="team-invite-drawer"
    >
      {inviteUrl === null ? (
        <form
          noValidate
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
          className="flex flex-col gap-4"
        >
          <FormField
            label={t('team.invite.email_label')}
            fieldId="team-invite-email"
            required
            error={emailError}
          >
            <Input
              id="team-invite-email"
              type="email"
              value={email}
              disabled={createM.isPending}
              invalid={emailError !== null}
              aria-describedby={emailError !== null ? 'team-invite-email-error' : undefined}
              placeholder={t('team.invite.email_placeholder')}
              onChange={(event) => {
                setEmail(event.currentTarget.value);
                setEmailError(null);
              }}
              data-testid="team-invite-email"
            />
          </FormField>

          <FormField label={t('team.invite.role_label')} fieldId="team-invite-role" required>
            <Select
              value={role}
              onValueChange={(next) => {
                if (next === 'member' || next === 'admin') setRole(next);
              }}
              options={[
                { value: 'member', label: t('team.invite.role.member') },
                { value: 'admin', label: t('team.invite.role.admin') },
              ]}
              disabled={createM.isPending}
              ariaLabel={t('team.invite.role_label')}
              testId="team-invite-role"
            />
          </FormField>

          {formError !== null ? (
            <p role="alert" data-testid="team-invite-error" className="m-0 text-label text-bad">
              {formError}
            </p>
          ) : null}

          <div className="mt-1 flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={createM.isPending}
              data-testid="team-invite-cancel"
            >
              {t('team.invite.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createM.isPending}
              data-testid="team-invite-submit"
            >
              {t('team.invite.submit')}
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-3" data-testid="team-invite-success">
          <p className="m-0 text-body font-medium text-paper">{t('team.invite.link_title')}</p>
          <p
            data-testid="team-invite-link-value"
            className="m-0 select-all break-all rounded bg-well px-3 py-2.5 font-mono text-dense text-paper"
          >
            {inviteUrl}
          </p>
          <p className="m-0 text-label text-paper-low">{t('team.invite.link_hint')}</p>
          <div className="mt-1 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="team-invite-done">
              {t('team.invite.done')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                void copy(inviteUrl);
              }}
              data-testid="team-invite-copy"
            >
              {copied ? t('team.invite.copied') : t('team.invite.copy')}
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function avatarInitial(member: OrgMemberSummary): string {
  const source = member.displayName ?? member.email;
  return source.trim().slice(0, 1).toUpperCase();
}

function apiKeyLabel(key: ApiKeySummary): string {
  const name = key.name?.trim() ?? '';
  return name.length > 0 ? name : key.prefix;
}

function filterMembers(
  members: readonly OrgMemberSummary[],
  search: string,
  unitFilter: UnitFilter,
): readonly OrgMemberSummary[] {
  const needle = search.trim().toLowerCase();
  return members.filter((member) => {
    if (unitFilter === UNASSIGNED && member.orgUnitId !== null) return false;
    if (unitFilter !== ALL && unitFilter !== UNASSIGNED && member.orgUnitId !== unitFilter) {
      return false;
    }
    if (needle.length === 0) return true;
    const haystack = [
      member.displayName ?? '',
      member.email,
      member.department ?? '',
      member.orgUnitName ?? '',
      member.orgUnitPath ?? '',
      member.role,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
}

function countMemberApiKeys(
  keys: readonly ApiKeySummary[],
  members: readonly OrgMemberSummary[],
): ReadonlyMap<string, number> {
  const memberAccounts = new Set(
    members.filter((member) => member.role !== 'owner').map((member) => member.accountId),
  );
  const counts = new Map<string, number>();
  for (const key of keys) {
    if (key.assignedAccountId === null || !memberAccounts.has(key.assignedAccountId)) {
      continue;
    }
    counts.set(key.assignedAccountId, (counts.get(key.assignedAccountId) ?? 0) + 1);
  }
  return counts;
}

function keysForMember(
  keys: readonly ApiKeySummary[],
  member: OrgMemberSummary,
): readonly ApiKeySummary[] {
  if (member.role === 'owner') return [];
  return keys.filter((key) => key.assignedAccountId === member.accountId);
}
